import "./PromoModals.css";
import React from 'react';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ModalShell from "../../../components/common/Modals/ModalShell";

/**
 * Renders a faithful preview of how a promo appears in the mobile app
 * (mirrors mobile-arcticspas-neo/src/components/PromoModal.js).
 *
 * The phone mockup is wrapped in a standard portal modal so it's clearly a
 * contained preview (with its own Close button) and not the live portal UI.
 *
 * The mobile modal is always a fixed dark theme, so these colors are the
 * mobile app's actual values (hot-tub flavor) — NOT the portal theme — so
 * the dealer sees exactly what the end user will see.
 */

// Mobile app palette (hot-tub flavor) — keep in sync with the mobile theme.
const MOBILE = {
  red: '#a32035',          // colors.arcticRed
  containerBg: '#231f20',  // colors.deepNavy.end
  iceWhite: '#f5f9fc',     // colors.iceWhite
  frost: '#dceef7',        // colors.frost
  border: 'rgba(255, 255, 255, 0.1)',
  ctaText: '#f5f9fc',
};

interface PromoLike {
  title?: string | null;
  body?: string | null;
  image_url?: string | null;
  url?: string | null;
  button_label?: string | null;
}

interface PromoPreviewModalProps {
  open: boolean;
  onClose: () => void;
  promo: PromoLike | null;
}

const PromoPreviewModal: React.FC<PromoPreviewModalProps> = ({ open, onClose, promo }) => {
  const title = promo?.title ?? '';
  const body = promo?.body ?? '';
  const imageUrl = promo?.image_url ?? null;
  const url = promo?.url ?? '';
  const buttonLabel = promo?.button_label ?? '';

  const hasUrl = !!url;
  const ctaLabel = buttonLabel || (hasUrl ? 'Learn More' : 'Dismiss');

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Mobile app preview"
      subtitle="How this promo appears to customers in the app."
      actions={<button className="btn btn-primary" onClick={onClose}>Close</button>}
    >
        <div className="promo-preview-stage">
          {/* Promo card — mirrors the mobile app's centered modal card */}
          <div
            style={{
              width: 320,
              maxWidth: '100%',
              height: 540,
              maxHeight: '62vh',
              backgroundColor: MOBILE.containerBg,
              borderRadius: 20,
              overflow: 'hidden',
              border: `1px solid ${MOBILE.border}`,
              boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'left',
            }}
          >
            {/* Scrollable area: image + content */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {/* Image */}
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Promo"
                  style={{
                    width: '100%',
                    minHeight: 220,
                    maxHeight: 300,
                    objectFit: 'contain',
                    display: 'block',
                    backgroundColor: MOBILE.containerBg,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  }}
                >
                  <CampaignOutlinedIcon sx={{ fontSize: 48, color: MOBILE.red }} />
                </div>
              )}

              {/* Content */}
              <div style={{ padding: 20 }}>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 'bold',
                    color: MOBILE.iceWhite,
                    marginBottom: 12,
                    letterSpacing: -0.3,
                    wordBreak: 'break-word',
                  }}
                >
                  {title || 'Promo title'}
                </div>
                <div
                  style={{
                    fontSize: 15,
                    color: MOBILE.frost,
                    lineHeight: '22px',
                    letterSpacing: 0.2,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {body || 'Promo body text will appear here.'}
                </div>
              </div>
            </div>

            {/* CTA / Dismiss button (visual only — inert in preview) */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                margin: '0 20px 20px',
                paddingTop: 16,
                paddingBottom: 16,
                borderRadius: 16,
                backgroundColor: hasUrl ? MOBILE.red : 'rgba(255, 255, 255, 0.1)',
                border: hasUrl ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  fontWeight: hasUrl ? 'bold' : 600,
                  color: hasUrl ? MOBILE.ctaText : MOBILE.frost,
                }}
              >
                {ctaLabel}
              </span>
              {hasUrl && <ArrowForwardIcon sx={{ fontSize: 18, color: MOBILE.ctaText }} />}
            </div>
          </div>
        </div>
    </ModalShell>
  );
};

export default PromoPreviewModal;
